import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { differenceInMinutes, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useMediaQuery, useTheme, Box } from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { safeSplit } from 'state/helpers';
import { useScrollSync } from 'hooks/useScrollSync';
import GanttLayout from './GanttLayout';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';


const GanttRoot = ({
    rowHeight = 50,
    cellWidth = 20,
    isShownCrewRoutes = false,
    flights = [],
    overrideAssignments = null,
}) => {
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate } = useDateRangeContext();

    const leftSize = useMemo(() => isMobile ? 21 : 7, [isMobile]);
    const rightSize = 100 - leftSize;

    const timelineScrollRef = useRef(null);
    const topLeftScrollRef = useRef(null);
    const topRightScrollRef = useRef(null);
    const bottomLeftScrollRef = useRef(null);
    const bottomRightScrollRef = useRef(null);

    const { syncX, syncY } = useScrollSync();

    const changedIds = useMemo(
        () => overrideAssignments ? new Set(overrideAssignments.keys()) : null,
        [overrideAssignments]
    );

    const chartData = useMemo(() => {
        if (!startDate || !flights?.length) return [];

        const getTime = (flight, field) =>
            timeMode === 'UTC' ? flight[field] : flight[`${field}LocalBase`];

        const baseDate = startOfDay(new Date(startDate));
        const toStartSlot = (slotDate) => {
            if (!slotDate) return 0;
            return differenceInMinutes(toZonedTime(slotDate, 'UTC'), baseDate) / 60;
        };

        const map = new Map();
        flights.forEach(f => {
            const effectiveReg = overrideAssignments?.get(f._id) ?? f.flightReg;
            const objectId = `${f.acType} ${effectiveReg}`;
            const acReg = effectiveReg?.split('-')[1] || effectiveReg || 'N/A';
            const label = `${f.acType} ${acReg}`;
            const slotDate = getTime(f, 'fullAtd') || getTime(f, 'fullEtd');
            const std = getTime(f, 'std');
            const sta = getTime(f, 'sta');
            const etd = getTime(f, 'etd');
            const eta = getTime(f, 'eta');
            const atd = getTime(f, 'atd') || etd || std;
            const ata = getTime(f, 'ata') || eta || sta;

            if (!map.has(objectId)) {
                map.set(objectId, { id: objectId, label, flights: [] });
            }

            map.get(objectId).flights.push({
                id: f._id,
                startSlot: toStartSlot(slotDate),
                duration: f.blockHours,
                type: f.sectorType,
                flightDesc: f.flightDesc,
                flightNo: f.flightNo,
                dep: f.dep,
                arr: f.arr,
                flightDate: f.flightDate,
                std, sta, etd, eta, atd, ata,
                stdDiff: f.stdDiff,
                staDiff: f.staDiff,
                expectedPax: safeSplit(f.expectedPaxPerCategory, 2),
                actualPax: safeSplit(f.paxPerCategory, 2),
                crewRoutes: f.crewRoutes,
            });
        });

        map.forEach(ac => {
            ac.flights.sort((a, b) => a.startSlot - b.startSlot);
        });

        return Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
    }, [timeMode, startDate, flights, overrideAssignments]);


    return (
        <Box
            sx={{
                height: "100%",
                minHeight: 0,
                borderRadius: 1,
                border: `1px solid ${theme.palette.neutral.deep}`,
            }}
        >
            <GanttLayout
                topDefaultSize={99.99}
                bottomCollapsible={true}
                topLeft={{
                    defaultSize: leftSize,
                    minSize: 0,
                    content: (
                        <LeftPanel
                            syncY={syncY}
                            topLeftScrollRef={topLeftScrollRef}
                            topRightScrollRef={topRightScrollRef}
                            bottomLeftScrollRef={bottomLeftScrollRef}
                            rowHeight={rowHeight}
                            headerName={"Aircraft"}
                            data={chartData}
                        />
                    ),
                }}
                topRight={{
                    defaultSize: rightSize,
                    minSize: 0,
                    content: (
                        <RightPanel
                            syncX={syncX}
                            syncY={syncY}
                            timelineScrollRef={timelineScrollRef}
                            topLeftScrollRef={topLeftScrollRef}
                            topRightScrollRef={topRightScrollRef}
                            bottomRightScrollRef={bottomRightScrollRef}
                            rowHeight={rowHeight}
                            cellWidth={cellWidth}
                            isShownCrewRoutes={isShownCrewRoutes}
                            data={chartData}
                            changedIds={changedIds}
                        />
                    ),
                }}
                bottomLeft={{
                    defaultSize: leftSize,
                    minSize: 0,
                    content: <Box ref={bottomLeftScrollRef} sx={{ height: '100%' }} />,
                }}
                bottomRight={{
                    defaultSize: rightSize,
                    minSize: 0,
                    content: <Box ref={bottomRightScrollRef} sx={{ height: '100%' }} />,
                }}
            />
        </Box>
    );
};

export default React.memo(GanttRoot);
